/**
 * Install Area Manuals section.
 *
 * - Accordion: clicking a header toggles its panel (data-iam-toggle).
 * - Brand chips: clicking a chip shows only the matching collection accordion
 *   (or all of them for the "all" chip).
 * - Carousel: prev/next arrows scroll each accordion's product row; arrow state
 *   is kept in sync with scroll/resize.
 *
 * Behaviour is delegated from `document` and guarded so it only initialises
 * once and survives re-renders.
 */
(() => {
  if (window.__installAreaManualsInit) return;
  window.__installAreaManualsInit = true;

  /* ---- PDF download ------------------------------------------------------ */
  // The PDFs live on cdn.shopify.com (a different origin), so the anchor's
  // `download` attribute is ignored and the file would open in a new tab —
  // which ad blockers / Chrome can kill (ERR_BLOCKED_BY_CLIENT). Instead we
  // fetch the file as a blob and trigger a real download in place.
  const filenameFor = (link, url) => {
    let name = link.getAttribute('download') || '';
    if (!name) {
      try {
        name = decodeURIComponent(url.split('?')[0].split('/').pop() || '');
      } catch (e) {
        name = '';
      }
    }
    if (!name) name = 'manual.pdf';
    if (!/\.pdf$/i.test(name)) name += '.pdf';
    return name;
  };

  document.addEventListener('click', async (event) => {
    const link = event.target.closest('[data-iam-pdf]');
    if (!link) return;

    const url = link.getAttribute('href');
    if (!url) return;

    // Let modified clicks (new tab/window, etc.) behave normally.
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;

    event.preventDefault();
    if (link.classList.contains('is-loading')) return;
    link.classList.add('is-loading');

    try {
      const res = await fetch(url, { credentials: 'omit' });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const tmp = document.createElement('a');
      tmp.href = objectUrl;
      tmp.download = filenameFor(link, url);
      document.body.appendChild(tmp);
      tmp.click();
      tmp.remove();
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    } catch (err) {
      // If the fetch is blocked or CORS fails, fall back to navigating to the
      // file in the same tab (still no popup for the blocker to kill).
      console.error('PDF download failed, navigating instead', err);
      window.location.href = url;
    } finally {
      link.classList.remove('is-loading');
    }
  });

  /* ---- Accordion toggle -------------------------------------------------- */
  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-iam-toggle]');
    if (!toggle) return;

    const acc = toggle.closest('[data-iam-acc]');
    if (!acc) return;

    const panel = acc.querySelector('.iam-acc__panel');
    const open = acc.classList.toggle('is-open');
    toggle.setAttribute('aria-expanded', String(open));
    if (panel) panel.hidden = !open;

    // Recompute arrow state once the panel is visible.
    if (open) {
      const carousel = acc.querySelector('[data-iam-carousel]');
      if (carousel) requestAnimationFrame(() => updateArrows(carousel));
    }
  });

  /* ---- Brand filter (shared by chips + mobile drawer) -------------------- */
  // Expand/collapse a single accordion and keep its header + carousel in sync.
  const setAccordionOpen = (acc, open) => {
    if (!acc || acc.classList.contains('is-open') === open) return;
    const toggle = acc.querySelector('[data-iam-toggle]');
    const panel = acc.querySelector('.iam-acc__panel');
    acc.classList.toggle('is-open', open);
    if (toggle) toggle.setAttribute('aria-expanded', String(open));
    if (panel) panel.hidden = !open;
    if (open) {
      const carousel = acc.querySelector('[data-iam-carousel]');
      if (carousel) requestAnimationFrame(() => updateArrows(carousel));
    }
  };

  // groups: array of group ids to show. Empty array or one containing 'all'
  // means "show everything".
  const applyFilter = (root, groups) => {
    const showAll = !groups || groups.length === 0 || groups.includes('all');
    const set = new Set(groups);

    root.querySelectorAll('[data-iam-acc]').forEach((acc) => {
      const visible = showAll || set.has(acc.dataset.iamGroup);
      acc.hidden = !visible;
      // When a specific brand filter is active, expand the matching collections
      // so their PDFs are shown without an extra click.
      if (!showAll && visible) setAccordionOpen(acc, true);
    });

    // Keep the desktop chips in sync with the active selection.
    root.querySelectorAll('[data-iam-chip]').forEach((chip) => {
      const value = chip.dataset.iamChip;
      const active = showAll ? value === 'all' : set.has(value);
      chip.classList.toggle('is-active', active);
      chip.setAttribute('aria-pressed', String(active));
    });
  };

  // Chips are single-select: clicking one shows just that collection.
  document.addEventListener('click', (event) => {
    const chip = event.target.closest('[data-iam-chip]');
    if (!chip) return;

    const root = chip.closest('[data-install-manuals]');
    if (!root) return;

    applyFilter(root, [chip.dataset.iamChip]);
  });

  /* ---- Mobile filter drawer ---------------------------------------------- */
  const drawerOf = (root) => root && root.querySelector('[data-iam-drawer]');

  const openDrawer = (root) => {
    const drawer = drawerOf(root);
    if (!drawer) return;

    // Seed the checkboxes from the groups currently shown.
    const shownGroups = [...root.querySelectorAll('[data-iam-acc]')]
      .filter((acc) => !acc.hidden)
      .map((acc) => acc.dataset.iamGroup);
    const allShown = shownGroups.length === root.querySelectorAll('[data-iam-acc]').length;
    drawer.querySelectorAll('.iam-drawer__checkbox').forEach((cb) => {
      cb.checked = !allShown && shownGroups.includes(cb.value);
    });

    drawer.hidden = false;
    document.body.style.overflow = 'hidden';
    requestAnimationFrame(() => drawer.classList.add('is-open'));
  };

  const closeDrawer = (drawer) => {
    if (!drawer) return;
    drawer.classList.remove('is-open');
    document.body.style.overflow = '';
    const onEnd = (event) => {
      if (event.target !== drawer.querySelector('.iam-drawer__panel')) return;
      drawer.hidden = true;
      drawer.removeEventListener('transitionend', onEnd);
    };
    drawer.addEventListener('transitionend', onEnd);
    // Fallback in case the transition doesn't fire.
    setTimeout(() => {
      if (!drawer.classList.contains('is-open')) drawer.hidden = true;
    }, 350);
  };

  document.addEventListener('click', (event) => {
    // Open
    const opener = event.target.closest('[data-iam-drawer-open]');
    if (opener) {
      openDrawer(opener.closest('[data-install-manuals]'));
      return;
    }

    // Close (overlay or X)
    const closer = event.target.closest('[data-iam-drawer-close]');
    if (closer) {
      closeDrawer(closer.closest('[data-iam-drawer]'));
      return;
    }

    // Collapse / expand the "Brand" group
    const toggle = event.target.closest('[data-iam-drawer-toggle]');
    if (toggle) {
      const group = toggle.closest('.iam-drawer__group');
      const collapsed = group.classList.toggle('is-collapsed');
      toggle.setAttribute('aria-expanded', String(!collapsed));
      return;
    }

    // Clear all — just uncheck; the selection commits on Apply.
    const clear = event.target.closest('[data-iam-drawer-clear]');
    if (clear) {
      const drawer = clear.closest('[data-iam-drawer]');
      drawer.querySelectorAll('.iam-drawer__checkbox').forEach((cb) => {
        cb.checked = false;
      });
      return;
    }

    // Apply
    const apply = event.target.closest('[data-iam-drawer-apply]');
    if (apply) {
      const drawer = apply.closest('[data-iam-drawer]');
      const root = apply.closest('[data-install-manuals]');
      const groups = [...drawer.querySelectorAll('.iam-drawer__checkbox:checked')].map((cb) => cb.value);
      applyFilter(root, groups);
      closeDrawer(drawer);
    }
  });

  // Esc closes any open drawer.
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const open = document.querySelector('.iam-drawer.is-open');
    if (open) closeDrawer(open);
  });

  /* ---- Carousel ---------------------------------------------------------- */
  const trackOf = (carousel) => carousel.querySelector('[data-iam-track]');

  const updateArrows = (carousel) => {
    const track = trackOf(carousel);
    if (!track) return;
    const prev = carousel.querySelector('[data-iam-prev]');
    const next = carousel.querySelector('[data-iam-next]');
    const max = track.scrollWidth - track.clientWidth;
    const overflowing = max > 2;
    if (prev) prev.disabled = !overflowing || track.scrollLeft <= 1;
    if (next) next.disabled = !overflowing || track.scrollLeft >= max - 1;
  };

  const step = (track) => {
    const card = track.querySelector('.iam-card');
    const cardWidth = card ? card.getBoundingClientRect().width : 174;
    return (cardWidth + 16) * 2; // two cards per click
  };

  document.addEventListener('click', (event) => {
    const prev = event.target.closest('[data-iam-prev]');
    const next = event.target.closest('[data-iam-next]');
    if (!prev && !next) return;

    const carousel = (prev || next).closest('[data-iam-carousel]');
    const track = carousel && trackOf(carousel);
    if (!track) return;

    track.scrollBy({ left: prev ? -step(track) : step(track), behavior: 'smooth' });
  });

  // Keep arrow state in sync while scrolling (scroll doesn't bubble -> capture).
  document.addEventListener(
    'scroll',
    (event) => {
      const track = event.target?.closest?.('[data-iam-track]');
      if (track) updateArrows(track.closest('[data-iam-carousel]'));
    },
    true
  );

  const observed = new WeakSet();
  const ro = new ResizeObserver((entries) => {
    for (const entry of entries) {
      const carousel = entry.target.closest('[data-iam-carousel]');
      if (carousel) updateArrows(carousel);
    }
  });

  const setup = () => {
    document.querySelectorAll('[data-iam-track]').forEach((track) => {
      if (!observed.has(track)) {
        ro.observe(track);
        observed.add(track);
      }
      updateArrows(track.closest('[data-iam-carousel]'));
    });
  };

  window.addEventListener('resize', () => setTimeout(setup, 60));

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setup);
  } else {
    setup();
  }
})();
